import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Upload, Trash2 } from "lucide-react";

async function fileToBase64(file) {
  return new Promise((res, rej) => {
    const reader = new FileReader();
    reader.onload = () => res(reader.result);
    reader.onerror = rej;
    reader.readAsDataURL(file);
  });
}

export default function Photos() {
  const [photos, setPhotos] = useState([]);
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/photos").then((r) => setPhotos(r.data));
  useEffect(() => {
    load();
  }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Max 5 MB");
      return;
    }
    setBusy(true);
    try {
      const b64 = await fileToBase64(file);
      await api.post("/photos", { image_b64: b64, caption });
      setCaption("");
      toast.success("Progress captured");
      load();
    } catch {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    await api.delete(`/photos/${id}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="photos-page">
      <div>
        <div className="label-tech">Progress Vault</div>
        <h1 className="h-display text-3xl sm:text-4xl mt-1">
          Physical <span className="text-cyan-400">Evolution</span>
        </h1>
      </div>

      <div className="panel panel-corners p-6">
        <label className="label-tech block mb-2">Caption (optional)</label>
        <input
          data-testid="photo-caption"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          className="w-full bg-black/60 border border-white/10 focus:border-cyan-400/60 outline-none px-3 py-2 text-white text-sm mb-3"
          placeholder="Day 30 - Cutting phase"
        />
        <label className="btn-neon inline-flex cursor-pointer">
          <Upload size={16} className="mr-2" />
          {busy ? "Uploading..." : "Upload Photo"}
          <input
            data-testid="photo-input"
            type="file"
            accept="image/*"
            onChange={onFile}
            className="hidden"
            disabled={busy}
          />
        </label>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {photos.map((p) => (
          <div key={p.id} className="panel p-2 group relative">
            <img src={p.image_b64} alt={p.caption || "progress"} className="w-full aspect-square object-cover" />
            <div className="mt-2 text-xs text-slate-300 truncate">{p.caption || "—"}</div>
            <div className="text-[0.6rem] font-mono-tech text-slate-500">{p.timestamp.slice(0, 10)}</div>
            <button
              onClick={() => del(p.id)}
              className="absolute top-3 right-3 p-1.5 bg-black/60 border border-red-500/40 text-red-400 opacity-0 group-hover:opacity-100 transition"
              data-testid={`photo-delete-${p.id}`}
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
      {photos.length === 0 && (
        <p className="text-sm text-slate-500 text-center py-8">
          No photos yet. Capture your baseline.
        </p>
      )}
    </div>
  );
}
